import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import CountrySelect from "../components/CountrySelect";
import { toast } from "sonner";
import { Plus } from "lucide-react";

export default function JobCreate() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    title: "", description: "", sector: "", contract_type: "CDI",
    country_code: "", city: "", remote: false,
    salary_min: "", salary_max: "", currency: "EUR",
    skills_required: "",
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.country_code) { toast.error("Sélectionnez un pays"); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        salary_min: form.salary_min ? Number(form.salary_min) : null,
        salary_max: form.salary_max ? Number(form.salary_max) : null,
        skills_required: form.skills_required.split(",").map((s) => s.trim()).filter(Boolean),
      };
      const r = await api.post("/jobs", payload);
      toast.success("Offre publiée !");
      nav(`/jobs/${r.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="job-create-page">
      <div className="mec-overline mb-2">Nouvelle offre</div>
      <h1 className="font-display text-3xl font-bold text-slate-900 mb-6">Publier une offre d'emploi</h1>
      <form onSubmit={submit} className="mec-card p-6 space-y-4">
        <div>
          <Label>Titre du poste</Label>
          <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="job-title" />
        </div>
        <div>
          <Label>Description</Label>
          <Textarea rows={8} required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="job-description" />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Secteur</Label>
            <Input value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} placeholder="Tech, Finance, Marketing..." data-testid="job-sector" />
          </div>
          <div>
            <Label>Type de contrat</Label>
            <Select value={form.contract_type} onValueChange={(v) => setForm({ ...form, contract_type: v })}>
              <SelectTrigger data-testid="job-contract"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="CDI">CDI</SelectItem>
                <SelectItem value="CDD">CDD</SelectItem>
                <SelectItem value="Freelance">Freelance</SelectItem>
                <SelectItem value="Stage">Stage</SelectItem>
                <SelectItem value="Alternance">Alternance</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Pays</Label>
            <CountrySelect value={form.country_code} onChange={(v) => setForm({ ...form, country_code: v })} testId="job-country" />
          </div>
          <div>
            <Label>Ville</Label>
            <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="job-city" />
          </div>
          <div>
            <Label>Salaire min.</Label>
            <Input type="number" value={form.salary_min} onChange={(e) => setForm({ ...form, salary_min: e.target.value })} data-testid="job-salary-min" />
          </div>
          <div>
            <Label>Salaire max.</Label>
            <Input type="number" value={form.salary_max} onChange={(e) => setForm({ ...form, salary_max: e.target.value })} data-testid="job-salary-max" />
          </div>
        </div>
        <div>
          <Label>Compétences requises (séparées par des virgules)</Label>
          <Input value={form.skills_required} onChange={(e) => setForm({ ...form, skills_required: e.target.value })} placeholder="Python, React, AWS..." data-testid="job-skills" />
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={form.remote} onCheckedChange={(v) => setForm({ ...form, remote: v })} data-testid="job-remote" />
          <Label className="cursor-pointer">Poste en télétravail</Label>
        </div>
        <Button type="submit" disabled={loading} className="bg-[#004EEB] hover:bg-[#003BCC] text-white" data-testid="submit-job">
          <Plus className="w-4 h-4 mr-1.5" /> {loading ? "Publication..." : "Publier l'offre"}
        </Button>
      </form>
    </div>
  );
}
